import {Injectable} from '@angular/core';
import {Injector} from '@angular/core';
import {Observable, Subject} from 'rxjs';
import {HttpService, JsonObject, JsonProperty, API_URL} from 'projects/core/src/public-api';
import {Order} from './order';
import {ShortObject} from 'projects/app-misc/src/app/modules/project-misc/data/project/short-json-object';
import {OrderItem} from './order-item';
import {Any} from 'json2typescript';
import {OrderEmail} from './order-email';
import {OrdersResponcePage} from './orders-responce-page';
import {Material} from '../../vendor/data/material';
import {OrderFilters} from './order-filter';
import {ShortProject} from 'projects/app-misc/src/app/modules/project-misc/data/project/short-project';
import {OrderStockInfo} from 'projects/desktop/src/app/modules/order/data/order-stock-info';

export class OrderFilter {
    vendors?: number[];
    project?: number[];
    statuses?: number[];
    start?: number[];
    limit?: number[];
}

export interface MaterialData {
    count?: number;
    cvMaterialId: number;
    materialId: number;
    backorder: boolean;
}


@Injectable({
    providedIn: 'root',
})
export class OrdersService extends HttpService {
    openedOrderSubj$ = new Subject<number>();

    public apiUrl: string;

    constructor(
        protected injector: Injector
    ) {
        super(injector);
        this.apiUrl = injector.get(API_URL);
    }

    getOrder(orderId: number): Observable<Order> {
        return this.getData(`/orders/${orderId}`, Order);
    }

    getOrders(): Observable<OrdersResponcePage> {
        return this.getData(`/orders/list`, OrdersResponcePage);
    }

    getOrderPdf(orderId: number): Observable<string> {
        return this.getData(`/orders/${orderId}/pdf`, String);
    }

    getOrderMessage(orderId: number): Observable<OrderEmail> {
        return this.getData(`/orders/${orderId}/email-body`, OrderEmail);
    }

    // tslint:disable-next-line: ban-types
    submitOrderWithMessage(orderId: number, message: String): Observable<Order> {
        return this.putData(`/orders/${orderId}/submit`, {message}, Order);
    }

    getFilteredOrders(filter: OrderFilter): Observable<OrdersResponcePage> {
        const params = [];

        for (const prop in filter) {
            if (Object.prototype.hasOwnProperty.call(filter, prop)) {
                const value = filter[prop] as number[]
                if (typeof value !== 'undefined') {
                    params.push(`${prop}=${value.join(',')}`)
                }
            }
        }

        return this.doHttpGet<OrdersResponcePage>(`/orders/list/filtered?${params.join('&')}`)
    }

    getOrderFilters(): Observable<OrderFilters> {
        return this.getData(`/cv/filters`, OrderFilters);
    }

    getProjectsForNewOrders(): Observable<ShortProject[]> {
        return this.getData(`/project/list/for-new-orders`, ShortProject);
    }

    getStockInfo(orderId: number): Observable<OrderStockInfo[]> {
        return this.getData(`/orders/${orderId}/reserve-of-material`, OrderStockInfo);
    }

    updateOrder(order: Order): Observable<Order> {
        return this.putData(`/orders`, order, Order);
    }

    updateOrderItem(order: Order): Observable<OrderItem[]> {
        return this.putData(`/orders/${order.id}/items`, order.items, OrderItem);
    }

    submitOrder(orderId: number): Observable<Order> {
        return this.putData(`/orders/${orderId}/submit`, {}, Order)
    }

    deleteOrder(orderId: number): Observable<void> {
        return this.deleteData(`/orders/${orderId}`, Any);
    }

    confirmOrder(orderId: number, items: MaterialData[], expectedDeliveryDate: Date, notes: string): Observable<Order> {
        return this.putData(`/orders/${orderId}/confirm`, {
            items,
            expectedDeliveryDate,
            notes
        }, Order)
    }

    acceptDelivery(orderId: number, items: MaterialData[], notes: string): Observable<Order> {
        return this.putData(`/orders/${orderId}/accept-delivery`, {
            items,
            notes
        }, Order)
    }

    newOrder(projectId: number, vendorId: number, items: MaterialData[], notes: string): Observable<Order> {
        return this.postData(`/orders`, {
            projectId,
            vendorId,
            items,
            notes
        }, Order)
    }

    getMaterials(projectId: number): Observable<Material[]> {
        return this.getData(`/project/${projectId}/materials/estimated`, Material);
    }

}
